import { useState, useEffect, useCallback, useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc, Organization } from 'src/trpc';
import { DataTable } from 'src/components/table/DataTable';
import { SortingState } from '@tanstack/react-table';
import { Search } from 'src/components/Search';
import { useSearchParams } from 'react-router-dom';
import { createOrganizationColumns } from '../Settings/columns/OrganizationColumns';
import { NewOrganizationDrawer } from './NewOrganizationDrawer';
import { useDebounce } from 'src/components/Debounce';
import { Loader } from 'src/components/Loader';
import { MainLayout } from '../MainLayout';
import { OrganizationDrawer } from 'src/components/OrganizationDrawer';

export const OrganizationDashboard = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [params, setParams] = useSearchParams({
    offset: '0',
    limit: '25',
    search: '',
    sortDirection: 'asc',
    sortKey: '',
  });

  const [search, setSearch] = useState(params.get('search') ?? '');
  const debouncedSearch = useDebounce(search, 500);

  // Drawer state management
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const offset = parseInt(params.get('offset') ?? '0');
  const limit = parseInt(params.get('limit') ?? '25');
  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  const { data, isLoading, isPending, isFetching } =
    trpc.admin.organization.getAllOrganizations.useQuery(
      {
        offset,
        limit,
        search: params.get('search') ?? '',
      },
      {
        placeholderData: keepPreviousData,
      },
    );

  const updateSearchParams = useCallback(
    (searchValue: string) => {
      const newParams = new URLSearchParams(params);
      newParams.set('search', searchValue);
      newParams.set('offset', '0');
      setParams(newParams);
    },
    [params, setParams],
  );

  useEffect(() => {
    const currentSearch = params.get('search') ?? '';
    if (debouncedSearch !== currentSearch) {
      updateSearchParams(debouncedSearch);
    }
  }, [debouncedSearch, params, updateSearchParams]);

  useEffect(() => {
    if (sorting.length) {
      const newParams = new URLSearchParams(params);
      newParams.set('sortDirection', sorting[0].desc ? 'desc' : 'asc');
      newParams.set('sortKey', sorting[0].id);
      newParams.set('offset', '0');
      setParams(newParams);
    }
  }, [sorting, params, setParams]);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleEditOrganization = useCallback((organization: Organization) => {
    setSelectedOrganization(organization);
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedOrganization(null);
  };

  const columns = useMemo(
    () => createOrganizationColumns(handleEditOrganization),
    [handleEditOrganization],
  );

  const transformedData = useMemo(
    () =>
      (data?.organizations || []).map((org) => ({
        ...org,
        onEdit: handleEditOrganization,
      })),
    [data?.organizations, handleEditOrganization],
  );

  if (isPending && isFetching) {
    return (
      <MainLayout>
        <Loader title="Loading organizations" />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Organizations</h2>
          <NewOrganizationDrawer />
        </div>

        <Search
          value={search}
          onChange={handleSearch}
          placeholder="Search organizations by name"
          isLoading={isLoading}
        />

        <div className="flex flex-col h-full">
          <DataTable
            total={data?.total || 0}
            resourceName="organizations"
            offset={offset}
            limit={limit}
            columns={columns}
            data={transformedData}
            currentPage={currentPage}
            sorting={sorting}
            onSortingChange={setSorting}
            hasNextPage={(data?.total || 0) > offset + limit}
            hasPreviousPage={offset > 0}
            onPageChange={(change) => {
              const newParams = new URLSearchParams(params);
              newParams.set('offset', (offset + change * limit).toString());
              setParams(newParams);
            }}
            pageCount={Math.ceil((data?.total || 0) / limit)}
            onRowClick={handleEditOrganization}
          />
        </div>
      </div>

      {/* Organization Drawer */}
      <OrganizationDrawer
        organization={selectedOrganization}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
      />
    </MainLayout>
  );
};
